<template>
  <!-- NOTIFICATION BELL ICON START -->
  <div class="tw-relative">
    <button
      v-if="!isMobile"
      class="tw-cursor-pointer tw-flex tw-items-center tw-gap-x-0.5 tw-text-gray-500 hover:tw-text-gray-900 focus:tw-text-gray-900 disabled:tw-text-gray-200"
      :disabled="!data.bellBtnActive"
      @click="toggleSidebar(!data.isOpen)"
    >
      <span
        :class="['material-icons tw-text-xl', hasUnread && 'tw-text-error-500']"
        >{{ hasUnread ? "notifications_active" : "notifications" }}</span
      >
      <span
        v-if="hasUnread"
        class="tw-text-error-500 tw-medium"
        >{{ unreadCount }}</span
      >
    </button>
    <button
      v-else
      class="tw-flex tw-gap-x-4 tw-items-center tw-py-3 tw-text-gray-900 tw-text-base tw-w-min"
      @click="toggleSidebar(!data.isOpen)"
    >
      <div class="tw-flex tw-items-center tw-gap-x-0.5">
        <span
          :class="[
            'material-symbols-outlined',
            hasUnread && 'tw-text-error-500',
          ]"
          >add_alert</span
        >
        <span
          v-if="hasUnread"
          class="tw-text-error-500 tw-medium"
          >{{ unreadCount }}</span
        >
      </div>
      <p class="tw-font-semibold">
        {{ __("notifications.notifications_title") }}
      </p>
    </button>
    <!-- NOTIFICATION BELL ICON END -->

    <!-- NOTIFICATION SIDEBAR OVERLAY -->
    <div
      class="tw-fixed tw-left-0 tw-top-0 tw-w-screen tw-h-screen tw-bg-black/50 tw-z-20"
      v-show="data.isOpen"
    ></div>

    <transition name="slide-in">
      <div
        v-if="data.isOpen"
        ref="notificationSidebar"
        class="tw-fixed tw-top-0 tw-right-0 tw-w-full md:tw-w-[400px] tw-bg-gray-50 tw-h-screen tw-flex tw-flex-col tw-z-20"
      >
        <!-- NOTIFICATION SIDEBAR HEADER START -->
        <div
          class="tw-px-4 tw-pt-6 tw-pb-3 tw-bg-white tw-border-b tw-border-b-gray-200 tw-flex tw-justify-between tw-items-start"
        >
          <div>
            <h5 class="tw-text-gray-900 tw-font-semibold tw-text-xl mb-1">
              {{ __("details.notifications") }}
            </h5>
            <p class="tw-text-sm tw-text-gray-600">
              {{ __("details.manage_notifications") }}
            </p>
          </div>
          <button
            ref="closeBtn"
            class="material-symbols-outlined tw-text-gray-500 tw-font-bold lg:tw-text-xl lg:tw-leading-none hover:tw-text-gray-700 focus-ring-gray"
            @click="toggleSidebar(false)"
          >
            close
          </button>
        </div>
        <!-- NOTIFICATION SIDEBAR HEADER END -->

        <!-- NOTIFICATION ITEMS START -->
        <div
          class="notification-scroll tw-flex tw-flex-col tw-divide-y tw-divide-y-gray-200 tw-grow"
          style="overflow-anchor: none"
          ref="notificationScroll"
          @scroll="handleScroll"
        >
          <div
            v-if="!data.notifications?.length && !data.isLoading"
            class="tw-text-center tw-p-5"
          >
            <img
              :src="asset('/assets/images/workperks/no-notification.svg')"
              :alt="__('details.no_notifications_title')"
            />
            <div class="tw-mt-3">
              <p class="tw-font-bold">
                {{ __("details.no_notifications_title") }}
              </p>
              <p>
                <small> {{ __("details.no_notifications") }} </small>
              </p>
            </div>
          </div>
          <template v-else>
            <button
              v-for="(notification, index) in data.notifications"
              :key="index"
              @click="triggerRedirect(notification)"
              class="tw-pt-6 tw-px-5 tw-pb-3 tw-cursor-pointer tw-flex tw-items-start tw-text-start hover:tw-bg-gray-100 focus:tw-bg-gray-100"
            >
              <img
                :src="notification.image"
                class="tw-w-12 tw-h-12 tw-rounded tw-bg-white tw-mr-3 tw-flex-shrink-0 tw-object-cover"
              />
              <div>
                <p class="tw-text-sm tw-font-medium tw-text-gray-700">
                  {{ notification.title }}
                  <small
                    class="tw-font-normal tw-text-xs tw-text-gray-600 tw-ml-2"
                    >{{ notification.human_date_interval }}</small
                  >
                </p>
                <p class="tw-text-sm tw-text-gray-600">
                  {{ truncate(notification.text, { length: 120 }) }}
                </p>
              </div>
              <span
                v-if="notification.status?.id === 'unread'"
                class="tw-ml-auto tw-w-2.5 tw-h-2.5 tw-bg-primary-500 tw-rounded-full tw-shrink-0"
              ></span>
            </button>
          </template>
          <div
            class="tw-m-auto"
            v-if="data.isLoading"
          >
            <div class="spinner-border tw-text-primary-600">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <!--  NOTIFICATION ITEMS END -->
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  watch,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  getCurrentInstance,
} from "vue";
import { API_ROUTES } from "lib/workperks/common/constants";
import axios from "axios";
import { paramsSerializer } from "lib/workperks/common/serializer";
import {
  TrackingAction,
  TrackingElement,
} from "lib/workperks/models/Analytics";
import analyticsService from "lib/workperks/services/AnalyticsService";
import { truncate } from "lodash";

defineProps({
  isMobile: {
    type: Boolean,
    default: false,
  },
});

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;
const asset = instance?.appContext.config.globalProperties.asset;

const notificationSidebar = ref(null);
const closeBtn = ref(null);
const notificationScroll = ref(null);

let fetchSession = 0;

const data = reactive({
  isOpen: false,
  initialLoad: true,
  notifications: [],
  showUnread: false,
  unreadIndex: [],
  bellBtnActive: false,
  notificationCount: 0,
  isLoading: false,
  notificationMeta: null,
  NotificationReqParams: {
    page: {
      size: 10,
      number: 1,
    },
  },
  csrfToken: window.s.csrfToken,
});

const unreadCount = computed(() => {
  return data.notificationCount < 10
    ? `0${data.notificationCount}`
    : data.notificationCount;
});

const hasUnread = computed(() => {
  return data.showUnread && data.notificationCount > 0;
});

const handleClickOutside = (event) => {
  if (!data.initialLoad && !notificationSidebar.value?.contains(event.target)) {
    toggleSidebar(false, true);
  } else if (data.isOpen) {
    data.initialLoad = false;
  }
};

const handleScroll = async () => {
  if (
    notificationScroll.value.scrollTop +
      notificationScroll.value.clientHeight +
      100 >=
    notificationScroll.value.scrollHeight
  ) {
    // Scrolling down
    if (
      data.notificationMeta.last_page >= data.NotificationReqParams.page.number
    ) {
      await getNotifications();
    }
  }
};

const toggleSidebar = async (toggle, initialLoad = true) => {
  data.initialLoad = initialLoad;
  data.isOpen = toggle;
  const body = document.body;
  if (!toggle) {
    fetchSession++;
    data.isLoading = false;
    data.notifications = [];
  }

  if (data.isOpen) {
    data.NotificationReqParams.page.number = 1;
    body.classList.add("body-no-scroll");
    let loadTimes = 1;
    if (!data.notifications.length) {
      // 80 is an average height for notif.btn. We can't take it from ref as it doesn't exist at this moment.
      const minVisible = Math.ceil(window.innerHeight / 80);
      loadTimes = Math.ceil(minVisible / data.NotificationReqParams.page.size);
    }

    do {
      await getNotifications();
      loadTimes -= 1;
    } while (
      loadTimes &&
      data.notificationMeta?.last_page >= data.NotificationReqParams.page.number
    );

    // track bell icon click
    trackNotificationImpression(
      {},
      TrackingAction.Click,
      TrackingElement.NotificationBell
    );
  } else {
    body.classList.remove("body-no-scroll");
  }
};

const getNotifications = async () => {
  if (data.isLoading) {
    return;
  }
  data.isLoading = true;
  const session = fetchSession;

  const params = {
    page: {
      number: data.NotificationReqParams.page.number,
      size: data.NotificationReqParams.page.size,
    },
  };
  try {
    const response = await axios.get(
      `${API_ROUTES.venngoApi}/workperks/notification/list`,
      {
        params,
        paramsSerializer,
        headers: {
          "x-api-token": window.s.apiToken,
        },
      }
    );

    if (session !== fetchSession) return;

    if (response.data?.data?.length) {
      data.notifications = data.notifications.concat(...response.data.data);
      data.notificationMeta = response.data.meta;
      data.NotificationReqParams.page.number++;
      notificationIsLoaded(data.notifications);
    }
  } catch (error) {
    if (error.response) {
      console.log("Notification Error: ", error.response.data);
    }
  } finally {
    if (session === fetchSession) {
      data.isLoading = false;
    }
  }
};

const getUnreadNotificationsCount = () => {
  axios
    .get(`${API_ROUTES.venngoApi}/workperks/notification/count`, {
      headers: {
        "x-api-token": window.s.apiToken,
      },
    })
    .then((response) => {
      if (response.data?.data) {
        data.notificationCount = response.data.data.count;
        data.bellBtnActive = true;
        data.showUnread = true;
      }
    })
    .catch((error) => {
      if (error?.response?.data) {
        // Server responded with a status other than 2xx
        console.log("Notification Error: ", error.response.data);
      } else if (error.request) {
        // Request was made but no response received
        console.log("Notification Error: No response received from server");
      } else {
        // Something else caused the error
        console.log("Notification Error: ", error.message);
      }
    });
};

const trackView = (notification) => {
  trackNotificationImpression(
    notification,
    TrackingAction.View,
    TrackingElement.NotificationList
  );
};

const triggerRedirect = (notification) => {
  // store selected notification in local storage for tracking
  localStorage.setItem(
    "selectedNotification",
    JSON.stringify({
      Status: notification.status.id,
      Type: notification.type.id,
    })
  );

  // Track notification click event
  trackNotificationImpression(
    notification,
    TrackingAction.Click,
    TrackingElement.NotificationList
  );
  updateStatus(notification);
};

const trackNotificationImpression = (
  notification,
  action,
  element_name = TrackingElement.NotificationPanel
) => {
  analyticsService
    .setTemplateId(null)
    .trackNotificationImpression(action, notification, notification.meta, element_name);
};

const notificationIsLoaded = (notifications = []) => {
  notifications.forEach((notification) => {
    trackView(notification);
  });
};

const updateStatus = async (notification) => {
  try {
    await axios.put(
      `${API_ROUTES.venngoApi}/workperks/notification/${notification.id}/status`,
      undefined,
      { headers: { "x-api-token": window.s.apiToken } }
    );

    if (!notification.meta.show_interstitial_page) {
      await axios.post(notification.cta_url, {
        _token: data.csrfToken,
        "X-Signature": notification.meta.xSignature,
        userId: notification.meta.user_id,
      });
    }

    window.open(notification.cta_url, "_blank", "noopener,noreferrer");
  } catch (error) {
    if (error?.response?.data) {
      console.log("Notification Status Update Failed ", error.response);
    }
  }
};

const handleKeyDown = (e) => {
  if (e.key === "Escape") {
    return toggleSidebar(false, true);
  }

  const isTabPressed = e.key === "Tab" || e.keyCode === 9;

  if (!isTabPressed) {
    return;
  }

  const focusableContent = notificationSidebar.value.querySelectorAll([
    "button:not([disabled])",
  ]);
  const firstFocusableElement = focusableContent[0];
  const lastFocusableElement = focusableContent[focusableContent.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === firstFocusableElement) {
      lastFocusableElement.focus();
      e.preventDefault();
    }
  } else if (document.activeElement === lastFocusableElement) {
    firstFocusableElement.focus();
    e.preventDefault();
  }
};

watch(
  () => data.isOpen,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      nextTick(() => {
        closeBtn.value.focus();
      });
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
  }
);

onMounted(() => {
  // call to get unread notification count after login
  if (window.s.user) {
    getUnreadNotificationsCount();
  }
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
